/**
 * Lightweight i18n for Método TAO (ES / CA).
 *
 * Translation dictionaries are split into per-area files under ./i18n/*.
 * This file only holds the Provider + hooks.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import translations from "./i18n/index";

export const LANGS = ["es", "ca"];
const DEFAULT = "es";
const STORAGE_KEY = "mt_lang";

const detect = () => {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS.includes(saved)) return saved;
  } catch (err) {
    // Private browsing / disabled storage: fall through to browser detection.
    console.warn("[i18n] localStorage read failed:", err);
  }
  const browser = (navigator.language || "es").toLowerCase().slice(0, 2);
  return LANGS.includes(browser) ? browser : DEFAULT;
};

const Ctx = createContext({ lang: DEFAULT, setLang: () => {}, t: (k) => k });

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(DEFAULT);

  // `detect` reads the saved language once on first paint; we don't want this
  // to re-run when locale changes, so the empty deps array is intentional.
  useEffect(() => { setLangState(detect()); }, []);
  // `lang` is the only changing input; setting <html lang> is a pure DOM write.
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => {
    // `LANGS` and `STORAGE_KEY` are module-level constants — stable across
    // renders, so they don't need to live in the deps array.
    if (!LANGS.includes(l)) return;
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch (err) {
      // Private browsing / disabled storage: language change still applies
      // for the current session, just won't persist.
      console.warn("[i18n] localStorage write failed:", err);
    }
  }, []);

  const t = useCallback((key, fallback) => {
    // `translations` and `DEFAULT` are module-level constants — see note above.
    const entry = translations[key];
    if (entry && entry[lang]) return entry[lang];
    if (entry && entry[DEFAULT]) return entry[DEFAULT];
    return fallback !== undefined ? fallback : key;
  }, [lang]);

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
};

export const useI18n = () => useContext(Ctx);
export const useT = () => useContext(Ctx).t;
export const useLang = () => useContext(Ctx).lang;
